/**
 * The выезд's volume as a whole — a 1:1 port of Android's `OrderVolume.kt`.
 *
 * Both of the city's limits apply to the **sum across every car**, exactly as the backend holds
 * them (`validate_total_volume`, `validate_trip_minimum`). Measuring either one against the
 * primary car alone produces two distinct bugs, and both were real:
 *
 * - two individually-acceptable cars overrun the ceiling together and are refused with a 400 on
 *   the payment screen, three steps from the field that could have fixed it;
 * - a 15 l car plus a 25 l car comfortably clear a 20 l minimum, but the step announced a
 *   surcharge that was never going to be charged.
 */
import type { ExtraCar, OrderDraft } from './draft';
import {
  FALLBACK_ESTIMATED_FULL_TANK_LITERS,
  FALLBACK_MAX_LITERS_MILLI,
  litersToWire,
  parseTypedLiters,
  parseWireLiters,
  validateLiters,
  type Milli,
} from './liters';

/**
 * What the primary car's `estimated_fuel_liters` will be on the wire.
 *
 * One definition shared by the quote, the create payload and {@link validateTotalVolume}: a total
 * computed from anything other than the numbers actually sent would police a different order than
 * the one the backend prices.
 */
export function estimatedFuelLiters(draft: OrderDraft): string {
  if (draft.fuelMode === 'fullTank') {
    return draft.tankVolumeLiters ?? FALLBACK_ESTIMATED_FULL_TANK_LITERS;
  }
  const typed = validateLiters(draft.liters, draft.tankVolumeLiters);
  if (typed.kind === 'valid') return litersToWire(typed.liters);
  return draft.tankVolumeLiters ?? FALLBACK_ESTIMATED_FULL_TANK_LITERS;
}

/**
 * How the выезд stands against the city's ceiling.
 *
 * Kept apart from `LitersValidation` deliberately: that one answers «is this field's number
 * acceptable» and renders under the field, this one answers «does the выезд as a whole fit the
 * city» and renders under the whole section.
 */
export type TotalVolumeValidation =
  | { kind: 'valid' }
  | { kind: 'exceedsCityMax'; total: Milli; max: Milli };

/**
 * A car whose litres are not a usable number yet resolves to valid: an unfinished field is the
 * per-car validation's business, and blaming the total for it would put two messages on screen
 * for one mistake.
 */
export function validateTotalVolume(draft: OrderDraft): TotalVolumeValidation {
  const cityMax = parseWireLiters(draft.maxOrderLiters);
  if (cityMax === null) return { kind: 'valid' };

  const total = orderedLitersTotal(draft);
  if (total === null) return { kind: 'valid' };
  if (total <= cityMax) return { kind: 'valid' };

  return { kind: 'exceedsCityMax', total, max: cityMax };
}

/**
 * How the выезд stands against the city's minimum.
 *
 * Three outcomes rather than two, because «below the minimum» is two different situations that
 * are handled differently — the same distinction the backend draws.
 */
export type MinOrderStatus =
  /** No city minimum, the выезд clears it, or somebody's volume is still unfinished. */
  | { kind: 'met' }
  /**
   * The tanks together **can** hold the minimum but the ordered volume does not reach it.
   *
   * That is a choice rather than a property of the cars, so the step does not advance until the
   * volume goes up or another car joins. Nobody should pay for 20 l and receive 15.
   */
  | { kind: 'shortOfMinimum'; minimum: Milli }
  /**
   * Every tank of the выезд together holds less than the minimum — there is physically nowhere
   * to put more fuel.
   *
   * The order goes through and is billed at the minimum. Refusing here would lock the service
   * away from small cars permanently. Both numbers are carried because saying «there will be a
   * surcharge» without naming them is the exact surprise this whole distinction exists to avoid.
   */
  | { kind: 'billedAtMinimum'; poured: Milli; minimum: Milli };

export function minOrderStatus(draft: OrderDraft): MinOrderStatus {
  const minimum = parseWireLiters(draft.minOrderLiters);
  if (minimum === null) return { kind: 'met' };

  // An unfinished field already has its own message underneath it; two messages for one mistake
  // is one too many.
  if (draft.fuelMode === 'liters' && validateLiters(draft.liters, draft.tankVolumeLiters).kind !== 'valid') {
    return { kind: 'met' };
  }

  const total = orderedLitersTotal(draft);
  if (total === null) return { kind: 'met' };
  if (total >= minimum) return { kind: 'met' };

  if (tripTankCapacity(draft) >= minimum) return { kind: 'shortOfMinimum', minimum };
  return { kind: 'billedAtMinimum', poured: total, minimum };
}

/**
 * What the выезд can hold in total.
 *
 * Summed across every car, not measured on one: two 12 l tanks together clear a 20 l minimum, and
 * then it is a choice again. A car with no declared tank counts as the same large fallback the
 * backend uses — an unknown tank is no reason to assume a small one.
 */
function tripTankCapacity(draft: OrderDraft): Milli {
  let capacity = parseWireLiters(draft.tankVolumeLiters) ?? FALLBACK_MAX_LITERS_MILLI;
  for (const extra of draft.extraCars) {
    capacity += parseWireLiters(extra.tankVolumeLiters) ?? FALLBACK_MAX_LITERS_MILLI;
  }
  return capacity;
}

/** The ordered volume across the whole выезд, or `null` while any car's field is unusable. */
export function orderedLitersTotal(draft: OrderDraft): Milli | null {
  let total = parseWireLiters(estimatedFuelLiters(draft));
  if (total === null) return null;
  for (const extra of draft.extraCars) {
    // The same RU-comma accommodation the primary field makes — an extra car's litres come from
    // the very same kind of free-text box.
    const litres = parseTypedLiters(extra.liters);
    if (litres === null) return null;
    total += litres;
  }
  return total;
}

/** Whether every extra car's own field is acceptable against its own tank. */
export function extraCarsValid(extras: ExtraCar[]): boolean {
  return extras.every((car) => validateLiters(car.liters, car.tankVolumeLiters).kind === 'valid');
}
